import styled, { css, DefaultTheme } from 'styled-components';
import { token } from '@bynder/design-system';
import { device, TEXT, VISUALS } from '../../../constants/styles';
import { TypographyHeading } from '../../../types/typography';
import { DEFAULT_FONT, getTypography, getTypographyWeight } from './constants';
import { WidgetPosition } from '../../../constants/widgets';
import { StyledTextWidget } from '../../widgets/TextWidget';
import { StyledTitleWidget } from '../../widgets/TitleWidget';
import { addQuotes, toPx } from '../../guides/Inspector/helpers';

type WidgetProps = {
  $readOnly: boolean;
  $isDragging?: boolean;
  $numberOfColumns?: number;
  $isInspectorEnabled?: boolean;
  $isSelectedWidget?: boolean;
  $isWidgetInspectorOpen?: boolean;
};

type ImageWidgetProps = {
  $isEmpty: boolean;
  $hasPreview: boolean;
  $extension?: string;
  $placeholder?: string;
  $borderRadius?: string;
};

export const StyledMediaWidget = styled.div<ImageWidgetProps>`
  display: flex;
  height: 100%;
  min-height: auto;
  max-width: 100%;
  margin: 0 auto;
  border-radius: ${props => toPx(props.$borderRadius)};
  overflow: hidden;
  align-items: center;
  justify-content: center;
  ${props => getTypography(TypographyHeading.NORMAL, props.theme, false)};

  & > * {
    display: flex;
    justify-content: center;
  }

  img {
    max-width: 100%;
  }

  video {
    width: 100%;
  }
  video[poster] {
    width: 100%;
  }

  ${props =>
    props.$isEmpty &&
    css`
      width: 100%;
      border: 1px dashed ${token.gray200a};
      background-color: ${token.white};

      & > div {
        min-height: calc(180px - 16px);
        flex: 1;
        align-items: center;
        margin: ${token.spacing3};
        border-radius: ${token.radiusMedium};
        white-space: break-spaces;

        &:first-child {
          margin-left: ${token.spacing3};
          margin-right: ${token.spacing2};
        }
        &:nth-child(2) {
          margin-left: ${token.spacing2};
          margin-right: ${token.spacing3};
        }

        &:hover {
          background-color: ${token.gray25};
          cursor: pointer;
          border-radius: ${token.radiusMedium};

          & > * {
            color: ${token.gray800a};
          }
        }

        & i {
          align-self: center;
          margin-bottom: ${token.spacing5};
        }
      }
    `}

  ${props =>
    !props.$hasPreview &&
    !props.$isEmpty &&
    css`
      border: 1px solid ${token.gray50a};
      background-color: ${token.gray25a};
      width: 100%;
    `}
`;

const editorToolbarTopSpacing = `-25px`;
const editorToolbarTop = `
  ${editorToolbarTopSpacing} +
  ${token.spacing3}
`;

export const StyledWidgetV2 = styled.li<WidgetProps>`
  position: relative;
  padding: 0;

  ${({ $readOnly, $isSelectedWidget, $isDragging, $isWidgetInspectorOpen }) =>
    !$readOnly &&
    ($isSelectedWidget || $isDragging) &&
    `outline: 3px ${$isWidgetInspectorOpen ? token.brandPrimary500 : token.brandPrimary100
    } solid;`}

  ${props =>
    props.$readOnly &&
    `
    &:empty {
      display: none;
    }
  `}

  ${StyledTextWidget},
  ${StyledTitleWidget},
  ${StyledMediaWidget} {
    word-break: break-word;
    flex-wrap: wrap;
  }

  ${(props: WidgetProps) =>
    props.$isDragging &&
    `background-color: white;
    `}

  .DraftEditor-editorContainer {
    z-index: unset;
  }

  .editor-wrapper {
    width: 100%;
    font-size: ${TEXT.FONT_SIZE_PARAGRAPH};
    ${props => getTypography(TypographyHeading.NORMAL, props.theme)};

    .editor-toolbar {
      padding: 4px;
      position: absolute;
      top: calc(${editorToolbarTop});
      z-index: ${VISUALS.ZINDEX_EDITOR_TOOLBAR};
      font-family: ${DEFAULT_FONT};
      font-weight: 400;
    }

    a {
      font-size: inherit;
      font-weight: inherit;
      font-family: inherit;
    }
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: ${token.gray800};
      margin: 0;
      line-height: 125%;
    }
    h1 {
      ${props => getTypography(TypographyHeading.H1, props.theme)};
    }
    h2 {
      font-size: ${TEXT.FONT_SIZE_TITLE};
      ${props => getTypography(TypographyHeading.H2, props.theme)};
    }
    h3 {
      font-size: ${TEXT.FONT_SIZE_SUBTITLE};
      ${props => getTypography(TypographyHeading.H3, props.theme)};

      span {
        font-weight: inherit;
      }
    }
    h4 {
      ${props => getTypography(TypographyHeading.H4, props.theme)};
    }
    h5 {
      ${props => getTypography(TypographyHeading.H5, props.theme)};
      margin-block-start: 0;
      margin-block-end: 0;
    }
    h6 {
      font-size: ${TEXT.FONT_SIZE_CAPTION};
      ${props => getTypography(TypographyHeading.H6, props.theme)};
    }

    .tea-wrapper {
      p {
        font-size: ${TEXT.FONT_SIZE_PARAGRAPH};
        ${props => getTypography(TypographyHeading.NORMAL, props.theme)};
      }
    }
  }
`;

export const StyledColorPaletteWidget = styled.div<{ widgetStyle: string }>`
  display: flex;
  flex-wrap: wrap;
  position: relative;
  @media ${device.tablet} {
    width: calc(100% + ${token.spacing3} * 2);
    margin: 0 calc(${token.spacing3} * -1);
  }
  ${props => props.widgetStyle}
`;

type ColorSwatchProps = {
  $numberOfColumns?: number;
  theme?: DefaultTheme;
  $widgetPosition: WidgetPosition;
};

export const StyledColorSwatch = styled.div<ColorSwatchProps>`
  ${props => getTypography(TypographyHeading.NORMAL, props.theme, false)};
  font-size: 1.15em;
  display: flex;
  flex-direction: column;
  max-width: 200px;
  padding: 0 ${token.spacing3};
  ${({ $widgetPosition }) => {
    switch ($widgetPosition) {
      case WidgetPosition.ONE_COLUMN:
      case WidgetPosition.TWO_COLUMNS_LARGE:
      case WidgetPosition.TWO_COLUMNS_EQUAL:
      case WidgetPosition.TWO_COLUMNS_SMALL:
      case WidgetPosition.THREE_COLUMNS_EQUAL:
      default:
        return 'flex: 0 0 100%';
    }
  }};
`;

export const SwatchTile = styled.div<{ $borderRadius: string }>`
  border-radius: ${props =>
    props.$borderRadius ? `${toPx(props.$borderRadius)}` : token.radiusMedium};
  height: 130px;
  width: 100%;
  border: 1px solid ${token.gray100a};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${token.white};

  &:hover > button {
    display: block;
  }
`;

export const SwatchColorName = styled.input<{
  $nameStyles: {
    fontFamily: string;
    fontSize: number;
    fontStyle: string;
    color: string;
  };
}>`
  background-color: inherit;
  font-size: ${token.fontSize100};
  line-height: 24px;
  border: 0 !important;
  outline: none;
  padding: ${token.spacing5} ${token.spacing2} ${token.spacing2}
    ${token.spacing2};
  width: 100%;

  color: ${props =>
    props.$nameStyles?.color && props.$nameStyles?.color
      ? props.$nameStyles?.color
      : token.gray800a};
  font-family: ${props =>
    props.$nameStyles?.fontFamily && addQuotes(props.$nameStyles?.fontFamily)};
  font-size: ${props =>
    props.$nameStyles?.fontSize && toPx(props.$nameStyles?.fontSize)};
  font-weight: ${props =>
    props.$nameStyles?.fontStyle &&
    getTypographyWeight(
      props.theme,
      props.$nameStyles?.fontFamily,
      props.$nameStyles?.fontStyle,
    )};

  &::placeholder {
    color: ${props =>
    props.$nameStyles?.color ? props.$nameStyles?.color : token.gray300a};
  }
`;

const colorKeyWidth = '80px';

type SwatchRowProps = {
  readOnly?: boolean;
  theme?: DefaultTheme;
};

export const SwatchRow = styled.div<SwatchRowProps>`
  height: 32px;
  padding: ${token.spacing3} ${token.spacing2} ${token.spacing2}
    ${token.spacing2};
  width: 100%;
  color: ${token.gray500a};
  font-size: ${token.fontSize75};
  display: inline-flex;
  ${props => getTypography(TypographyHeading.NORMAL, props.theme, false)};

  > *:first-child {
    width: ${colorKeyWidth};
    margin-right: ${token.spacing2};
  }

  > *:last-child {
    width: calc(100% - ${colorKeyWidth} - ${token.spacing2});
  }

  input {
    border: 0;
    padding: 0;
    outline: none;
    background-color: inherit;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    &[readOnly] {
      cursor: pointer;
      border: 0;
    }

    ${props =>
    !props.readOnly &&
    `
      &:hover,
      &:focus,
      &:focus-within {
        background-color: ${token.gray50a};
        border-radius: ${token.radiusSmall};
        border: 0;
      }
    `}
  }
`;

export const CustomSwatchRow = styled(SwatchRow)`
  span {
    margin: 1px ${token.spacing2} 0 0;
    display: inline-block;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

export const AddSwatchColor = styled.a`
  height: 130px;
  width: 100%;
  border: 1px dashed ${token.gray200a};
  border-radius: ${token.radiusMedium};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: ${token.white};
`;

export const StyledAudioWidget = styled.div<{
  $isEmpty: boolean;
  readOnly: boolean;
}>`
  width: 100%;
  height: 112px;
  border-radius: ${token.radiusLarge};
  background-color: ${token.gray25a};

  audio {
    width: 100%;
    height: 40px;
    border-radius: ${token.radiusMedium};
    outline: 0;
  }

  ${props =>
    props.readOnly &&
    `
      padding: 0 ${token.spacing5};
    `};

  > a {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    border: 0;
    box-shadow: none;

    ${props =>
    props.$isEmpty &&
    `
      border: 1px dashed ${token.gray200a};
      background-color: ${token.white};
      justify-items: center;
      flex-direction: row;
      align-items: center;

      > :only-child {
        flex-grow: 1;
      }
  `};
  }
`;

export const StyledAudioHeader = styled.div`
  display: grid;
  text-align: left;
  height: 32px;
  color: ${token.gray500a};
  line-height: 32px;
  margin-bottom: ${token.spacing4};
`;

export const StyledAudioName = styled.span`
  color: ${token.gray800a};
  font-size: ${token.fontSize200};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
